import { Check, ImagePlus, Upload } from "lucide-react";
import { useRef, useState } from "react";
import type { MediaPhoto } from "../types";
import { Spinner } from "./Spinner";

interface Props {
  photos: MediaPhoto[];
  value: string | null;
  onChange(id: string | null, photo?: MediaPhoto): void;
  /** When provided, a drop zone lets the user add their own photo. */
  onUpload?(file: File): Promise<MediaPhoto>;
  allowNone?: boolean;
  label?: string;
}

/** Photo chooser from the "Foto de tu sueño" design: current selection, catalogue thumbnails, and a drop zone. */
export function PhotoPicker({ photos, value, onChange, onUpload, allowNone = false, label = "Elige una foto" }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const upload = async (file: File | undefined) => {
    if (!file || !onUpload) return;
    if (file.size > 8 * 1024 * 1024) { setError("La imagen supera 8 MB."); return; }
    setUploading(true); setError(null);
    try {
      const photo = await onUpload(file);
      onChange(photo.id, photo);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No pudimos subir la imagen.");
    } finally { setUploading(false); }
  };

  return (
    <div className="photo-picker">
      <div className="photo-grid" role="listbox" aria-label={label}>
        {allowNone && (
          <button type="button" role="option" aria-selected={value === null} className={`photo-option none${value === null ? " selected" : ""}`} onClick={() => onChange(null)}>
            <span>Sin foto</span>
          </button>
        )}
        {photos.map((photo) => (
          <button
            key={photo.id}
            type="button"
            role="option"
            aria-selected={value === photo.id}
            className={`photo-option${value === photo.id ? " selected" : ""}`}
            style={{ backgroundImage: `url(${photo.url})` }}
            onClick={() => onChange(photo.id, photo)}
            title={photo.title ?? undefined}
          >
            {value === photo.id && <span className="photo-check"><Check size={14} strokeWidth={3} aria-hidden="true" /></span>}
            {photo.title && <span className="photo-title">{photo.title}</span>}
          </button>
        ))}
      </div>
      {onUpload && (
        <div
          className={`photo-drop${dragging ? " dragging" : ""}`}
          onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => { event.preventDefault(); setDragging(false); void upload(event.dataTransfer.files[0]); }}
        >
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(event) => { void upload(event.target.files?.[0]); event.target.value = ""; }} />
          {uploading ? <Spinner label="Subiendo imagen" /> : <ImagePlus size={22} aria-hidden="true" />}
          <strong>Arrastra una foto o elige del catálogo</strong>
          <span>JPG, PNG o WebP · hasta 8 MB</span>
          <button type="button" className="secondary-button small" onClick={() => inputRef.current?.click()} disabled={uploading}>
            <Upload size={14} aria-hidden="true" /> Subir foto
          </button>
        </div>
      )}
      {error && <div className="form-error" role="alert">{error}</div>}
    </div>
  );
}
